import { useUserState } from '@clarity-website/common/UserProvider';
import ClarityClient from '@clarity-website/lib/clients/ClarityClient';
import useClarityClient from '@clarity-website/lib/clients/useClarityClient';
import { oneDayInMs } from '@clarity-website/utils/cache-time-constants';
import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-tag';

// EmployeeInfo shape required by AZA's AzaGlobalFab (@amzn/atoz-assistant-component).
// Non-AtoZ portals must pass this; without it AZA reads window.AtoZContext, which
// Clarity's own origin never injects.
export interface AzaEmployeeInfo {
  authenticatedEmployeeId: string;
  employeeId: string;
  countryCode: string;
  locale: string;
  firstName?: string;
  lastName?: string;
  alias?: string;
}

interface GetUserProfileResponse {
  getUserProfile: {
    employee: {
      basicInfo: { legacyEmployeeId: string | null; login: string };
    } | null;
  } | null;
}

// Resolves the signed-in alias to its legacy employee id via PAPI (server-side).
const GET_AZA_EMPLOYEE_ID = gql`
  query getAzaEmployeeId($userId: String!) {
    getUserProfile(userId: $userId) {
      employee {
        basicInfo {
          legacyEmployeeId
          login
        }
      }
    }
  }
`;

async function fetchLegacyEmployeeId(
  client: ClarityClient,
  login: string,
): Promise<string | undefined> {
  const { getUserProfile } = await client.gql<GetUserProfileResponse>({
    request: GET_AZA_EMPLOYEE_ID,
    variables: { userId: login },
  });
  return getUserProfile?.employee?.basicInfo?.legacyEmployeeId ?? undefined;
}

// Builds the EmployeeInfo AZA needs, or undefined until the id resolves.
export default function useAzaEmployeeInfo(): AzaEmployeeInfo | undefined {
  const client = useClarityClient();
  const { user } = useUserState();
  const login = user?.login;

  const { data: employeeId } = useQuery(
    ['aza-employee-id', login],
    () => fetchLegacyEmployeeId(client, login as string),
    {
      enabled: Boolean(login),
      // QueryClient defaults suspense:true; this hook runs above its own
      // Suspense boundary, so opt out to return undefined while loading.
      suspense: false,
      cacheTime: oneDayInMs,
      staleTime: oneDayInMs,
    },
  );

  if (!user || !login || !employeeId) {
    return undefined;
  }

  return {
    authenticatedEmployeeId: employeeId,
    employeeId,
    countryCode: user.country,
    locale: navigator.language,
    firstName: user.firstName,
    lastName: user.lastName,
    alias: login,
  };
}
